import {
  SALES_DEFAULT_DAYS,
  isSeriesDate,
  salesBucketSpan,
  shiftDate,
  type IsoDate,
  type OrderSeriesBucket,
  type SalesPaymentStatus,
} from "@estelle/domain";

import { formatDate } from "@/modules/gst/format";
import {
  DEFAULT_CHART,
  RANGE_TOO_WIDE,
  START_AFTER_END,
  bucketDetail,
  bucketLabel,
  decodeChart,
  encodeChart,
  presetRange,
  spanLabel,
  type RangePreset,
} from "@/modules/reports/orderChart";
import type { DateRangePreset } from "@/modules/reports/DateRangeControls";

/*
  The Sales report is counted on the server (`orders.salesSummary`, #486), over
  a range of the store's days. What is left here is what the page needs to ask
  for one and to word the answer: the range, how it lives in the URL, and the
  labels.
*/

type Range = { from: IsoDate; to: IsoDate };

/**
 * The range in the address bar, if it names one, and why not if it names one
 * that cannot be used.
 *
 * It lives in the same `chart` parameter the Orders report keeps its settings
 * in — "what a report's chart is set to" — and is read by the same tolerant
 * decoder, so a link someone truncated or edited by hand opens the default
 * range rather than an error, and a range the API would refuse is never sent.
 *
 * `problem` is for the screen to say: a range that was asked for and is not
 * the one shown has to be explained, or the dates in the address bar and the
 * ones on the page quietly differ. A link with no dates, or none of this
 * report's, has nothing to explain.
 */
export function readSalesRange(raw: string | undefined): {
  range: Range | null;
  problem: string | null;
  /** The "Include cancelled orders" switch, kept in the same parameter. */
  includeCancelled: boolean;
} {
  const { range, includeCancelled } = decodeChart(raw);
  if (range || !raw) return { range, problem: null, includeCancelled };

  const parts = new Map<string, string>();
  for (const pair of raw.split(",")) {
    const colon = pair.indexOf(":");
    if (colon > 0) parts.set(pair.slice(0, colon), pair.slice(colon + 1));
  }
  const from = parts.get("from");
  const to = parts.get("to");
  if (from === undefined && to === undefined) {
    return { range, problem: null, includeCancelled };
  }

  if (!isSeriesDate(from) || !isSeriesDate(to)) {
    return {
      range,
      problem: "The dates in the link were not understood.",
      includeCancelled,
    };
  }
  // Real dates, so the decoder refused them for one of its two other reasons.
  return {
    range,
    problem: from > to ? START_AFTER_END : RANGE_TOO_WIDE,
    includeCancelled,
  };
}

/**
 * The `chart` parameter for a range and the cancelled switch: `undefined` for
 * the defaults, so the bar is bare.
 */
export function encodeSalesRange(
  range: Range | null,
  includeCancelled = false,
): string | undefined {
  return encodeChart({ ...DEFAULT_CHART, range, includeCancelled });
}

/** The dates the report is for: the link's, else the last 60 days to `today`. */
export function effectiveSalesRange(
  range: Range | null,
  today: IsoDate,
): Range {
  return range ?? { from: shiftDate(today, 1 - SALES_DEFAULT_DAYS), to: today };
}

const PRESET_LABELS: readonly { id: RangePreset | "last-60"; label: string }[] =
  [
    { id: "last-7", label: "Last 7 days" },
    { id: "last-30", label: "Last 30 days" },
    // What the report has always opened on.
    { id: "last-60", label: "Last 60 days" },
    { id: "last-90", label: "Last 90 days" },
    { id: "this-month", label: "This month" },
    { id: "last-month", label: "Last month" },
  ];

/** The buttons above the report, as the dates each means today. */
export function salesPresets(today: IsoDate): DateRangePreset[] {
  return PRESET_LABELS.map(({ id, label }) => ({
    id,
    label,
    ...(id === "last-60"
      ? { from: shiftDate(today, -59), to: today }
      : presetRange(id, today)),
  }));
}

/** What the x axis is titled for a bar of each size. */
export const BUCKET_AXIS_LABELS: Record<OrderSeriesBucket, string> = {
  day: "Date",
  week: "Week starting",
  month: "Month",
};

/**
 * What a bar is called on the axis and in the tooltip.
 *
 * A week or month at the edge of the range is one whole bar that holds only the
 * days inside it, and says so: the tooltip names the days it really holds and
 * calls it partial, and a partial week's axis label is its first day inside
 * the range rather than the Monday before it, so no bar claims days it does
 * not count.
 */
export function bucketLabels(
  date: IsoDate,
  bucket: OrderSeriesBucket,
  range: Range,
): { label: string; detail: string } {
  const span = salesBucketSpan(date, bucket, range);
  const axis = bucket === "month" ? bucketLabel(date, bucket) : undefined;
  if (!span.partial) {
    return {
      label: axis ?? formatDate(date),
      detail: bucketDetail(date, bucket),
    };
  }
  return {
    label: axis ?? formatDate(span.from),
    detail: `${spanLabel(span.from, span.to)} (partial ${bucket})`,
  };
}

const STATUS_LABELS: Record<SalesPaymentStatus, string> = {
  pending: "Pending",
  authorized: "Authorized",
  partially_paid: "Partially paid",
  paid: "Paid",
  partially_refunded: "Partially refunded",
  refunded: "Refunded",
  voided: "Voided",
  unknown: "Unknown",
};

export function paymentStatusLabel(status: SalesPaymentStatus): string {
  return STATUS_LABELS[status];
}
